import { openDeleteModalForCalendarEvent } from '../actions/openDeleteModal';
import { observer } from 'mobx-react-lite';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { lazyShowFullComposeInEditMode } from 'owa-calendar-compose-form-store';
import { getFullCalendarEvent } from 'owa-calendar-forms-common/lib/helpers/eventStore';
import { FULL_COMPOSE_VIEW_TYPE_MODAL } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import { getCommands } from 'owa-calendar-hotkeys';
import { requestModuleCommand } from 'owa-calendar-module-command';
import { getStore as getPeekStore } from 'owa-calendar-peek';
import { CalendarItemPeekCallout, CalendarItemPeekCalloutHandle } from 'owa-calendar-peek-callout';
import { lazyShowReadingPane } from 'owa-calendar-reading-pane-actions';
import { lockSelectedItem } from 'owa-calendar-surface';
import { CalendarSurfaceHandle } from 'owa-calendar-surface-core';
import { surfaceStore } from 'owa-calendar-surface-surface-store';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getUserMailboxInfo } from 'owa-client-ids';
import { useGlobalHotkey } from 'owa-hotkeys';
import EventScope from 'owa-service/lib/contract/EventScope';
import * as React from 'react';
import { useRepositionableCallout } from 'owa-react-hooks/lib/useRepositionableCallout';
import { isFeatureEnabled } from 'owa-feature-flags';

// TODO: VSO #88707 Separate module vs surface concerns in Calendar module

/**
 * Props for item peek renderer
 */
export interface ItemPeekRendererProps {
    surfaceHandle: CalendarSurfaceHandle;
    shouldCoverTarget: (itemId: string) => boolean;
}

/**
 * Component for rendering item peek
 */
export default observer(function ItemPeekRenderer(props: ItemPeekRendererProps) {
    const calloutRef = React.useRef<CalendarItemPeekCalloutHandle>();
    const deleteEvent = () => {
        const itemPeekStore = getPeekStore();
        let itemId = itemPeekStore.viewState ? itemPeekStore.viewState.itemId : null;
        if (!itemId) {
            return null;
        }
        let item = getFullCalendarEvent(null, itemId);
        lockSelectedItem(surfaceStore);
        calloutRef.current.onCalloutDismiss();
        openDeleteModalForCalendarEvent(item, false, 'SurfacePeek');
    };

    useGlobalHotkey(getCommands().deleteEvent, deleteEvent);

    let bounds: ClientRect = undefined;
    // VSO 24629: in day, week, work week views, the item peek should be confined to the scroll well so it doesn't overlap the all day well
    if (surfaceStore.dateRangeType !== DateRangeType.Month) {
        let targetElement = document.querySelector(
            surfaceStore.selectedItemCSSSelector
        ) as HTMLElement;
        bounds = props.surfaceHandle?.getPopupBounds(targetElement);
    }

    const {
        onMouseDown,
        onPositioned,
        calloutStyles,
        manuallyRepositioned,
    } = useRepositionableCallout();

    return (
        <CalendarItemPeekCallout
            ref={calloutRef}
            target={surfaceStore.selectedItemCSSSelector}
            bounds={bounds}
            shouldCoverTarget={props.shouldCoverTarget}
            onEditEventClicked={onEditEventClicked}
            onEventDeleted={onEventDeleted}
            onOpenEventClicked={onOpenEventClicked}
            onReviewProposedNewTimeClicked={onReviewProposedNewTimeClicked}
            viewType={'SurfacePeek'}
            entrySource={'Peek_Edit'}
            onPositioned={onPositioned}
            styles={calloutStyles}
            onMouseDown={isFeatureEnabled('cal-surface-dragPeek') ? onMouseDown : undefined}
            beakWidth={manuallyRepositioned ? 0 : undefined} // if repositioned manually, hide the beak
        />
    );
});

function onOpenEventClicked(ev?: React.MouseEvent<HTMLElement>, shouldScrollToInsights?: boolean) {
    const { itemId } = getPeekStore().viewState;
    lazyShowReadingPane.importAndExecute(
        { Id: itemId, mailboxInfo: getUserMailboxInfo() },
        'Peek',
        false /* getSeriesMaster */,
        false /* isDeeplink */,
        true /* showModal */,
        false /*isInPopoutWindow */,
        shouldScrollToInsights
    );
}

function onEditEventClicked() {
    lockSelectedItem(surfaceStore);
}

function onEventDeleted() {
    requestModuleCommand(null);
}

function onReviewProposedNewTimeClicked(newEvent?: Partial<CalendarEvent>) {
    const { itemId } = getPeekStore().viewState;
    const event = getFullCalendarEvent(null, itemId);
    lazyShowFullComposeInEditMode.importAndExecute(
        {
            ...event,
            ...newEvent,
        },
        EventScope.Default,
        true /* itemHasUpdates */,
        'PeekReviewPNT',
        FULL_COMPOSE_VIEW_TYPE_MODAL,
        null /* extractionSourceId */
    );
}
